import { NgFor, NgIf } from '@angular/common';
import { Component, DoCheck, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { dataLocalParaInput, LocalDatePipe } from '../../core/date-utils';
import { Cliente, Contagem, ContagemItem, NotaRetirada, NotaTransferencia, NotaTransferenciaItem, Produto } from '../../core/models';
import { statusLabel } from '../../core/status-labels';

type OrdemEntradaFormItem = { id_produto: number };

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, RouterLink, LocalDatePipe],
  template: `
    <header class="page-header">
      <div><h2>Estoque por cliente</h2><p>Controle do estoque recebido, contado e retirado por cliente</p></div>
    </header>

    <nav class="tabs" aria-label="Abas de estoque por cliente">
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'consulta'" (click)="selecionarAba('consulta')">Consulta</button>
      <button class="tab-button" type="button" [class.active]="abaAtiva === 'retiradas'" (click)="selecionarAba('retiradas')">Retiradas</button>
    </nav>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span>
        <button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <section class="panel" *ngIf="abaAtiva === 'inclusao'">
      <form [formGroup]="form" (ngSubmit)="salvar()" novalidate>
        <div class="grid-form">
          <input type="hidden" formControlName="id_nota">
          <label>Cliente
            <select formControlName="id_cliente">
              <option value="">Selecione</option>
              <option *ngFor="let cliente of clientes" [value]="cliente.id_cliente">{{ cliente.nome }}</option>
            </select>
          </label>
          <label>Número da ordem<input formControlName="numero_nota" placeholder="Gerado automaticamente" readonly></label>
          <label>Data da ordem<input type="date" formControlName="data_nota"></label>
          <label>Recebimento<input type="date" formControlName="data_recebimento"></label>
          <label>Cor<input formControlName="cor"></label>
          <label class="full">Observação<textarea rows="2" formControlName="observacao"></textarea></label>
        </div>

        <div class="inline-picker">
          <label>Produto
            <select formControlName="produto_para_adicionar">
              <option value="">Selecione</option>
              <option *ngFor="let produto of produtosDisponiveis" [value]="produto.cod_produto">{{ produto.codigo_produto }} - {{ produto.descricao }}</option>
            </select>
          </label>
          <button class="ghost" type="button" (click)="adicionarProduto()" [disabled]="!form.controls.produto_para_adicionar.value">Adicionar produto</button>
        </div>

        <table class="size-table" formArrayName="itens" *ngIf="itens.length">
          <thead><tr><th>Produto</th><th></th></tr></thead>
          <tbody>
            <tr *ngFor="let item of itens.controls; let i = index" [formGroupName]="i">
              <td data-label="Produto">{{ produtoLabel(item.value.id_produto) }}</td>
              <td class="actions" data-label="Ações"><button class="ghost" type="button" (click)="removerProduto(i)" [disabled]="produtoTemConferencia(item.value.id_produto)">Remover</button></td>
            </tr>
          </tbody>
        </table>
        <p class="field-hint" *ngIf="!itens.length">Nenhum produto selecionado</p>

        <div class="form-actions">
          <button type="submit">Salvar ordem</button>
          <button class="ghost" type="button" (click)="limpar()">Limpar</button>
        </div>
      </form>
    </section>

    <section *ngIf="abaAtiva === 'retiradas'">
      <nav class="tabs" aria-label="Abas de retiradas">
        <button class="tab-button" type="button" [class.active]="abaRetiradaAtiva === 'consulta'" (click)="selecionarAbaRetirada('consulta')">Consulta</button>
        <button class="tab-button" type="button" [class.active]="abaRetiradaAtiva === 'edicao'" (click)="selecionarAbaRetirada('edicao')">Inserir/Editar retirada</button>
      </nav>

      <table class="data-table" *ngIf="abaRetiradaAtiva === 'consulta'">
        <thead>
          <tr><th>Retirada</th><th>Data</th><th>Estoque</th><th>Cliente</th><th>Contagens</th><th>Responsavel</th><th>Total</th><th>Observacao</th><th></th></tr>
        </thead>
        <tbody>
          <tr *ngFor="let retirada of retiradas">
            <td data-label="Retirada"><strong>#{{ retirada.id_retirada }}</strong></td>
            <td data-label="Data">{{ retirada.data_retirada | localDate }}</td>
            <td data-label="Estoque">{{ estoqueLabel(retirada.numero_nota) }}</td>
            <td data-label="Cliente">{{ retirada.cliente }}</td>
            <td data-label="Contagens">{{ contagensRetiradaLabel(retirada) }}</td>
            <td data-label="Responsavel">{{ retirada.responsavel || '-' }}</td>
            <td data-label="Total">{{ retirada.total_pecas || totalRetirada(retirada.itens) }}</td>
            <td data-label="Observacao">{{ retirada.observacao || '-' }}</td>
            <td class="actions" data-label="Acoes">
              <button type="button" (click)="editarRetirada(retirada)">Editar</button>
              <button class="ghost" type="button" (click)="imprimirRetirada(retirada)">Imprimir</button>
              <button class="ghost danger-action" type="button" (click)="excluirRetirada(retirada)">Excluir</button>
            </td>
          </tr>
        </tbody>
      </table>

      <section class="panel" *ngIf="abaRetiradaAtiva === 'edicao'">
        <h3>Inserir/Editar retirada</h3>
        <form [formGroup]="retiradaForm" (ngSubmit)="registrarRetiradaGeral()" novalidate>
          <div class="grid-form">
            <label>Data retirada<input type="date" formControlName="data_retirada"></label>
            <label>Responsavel entrega<input formControlName="responsavel"></label>
            <label class="full">Observacao<textarea rows="2" formControlName="observacao"></textarea></label>
          </div>

          <table class="data-table" *ngIf="contagensParaRetiradaForm.length">
            <thead><tr><th></th><th>Contagem</th><th>Estoque</th><th>Cliente</th><th>Produto</th><th>Data</th><th>Responsaveis</th><th>Total</th></tr></thead>
            <tbody>
              <tr *ngFor="let contagem of contagensParaRetiradaForm" [class.selected-row]="contagemRetiradaSelecionada(contagem.id_contagem)">
                <td data-label="Selecionar"><input type="checkbox" [checked]="contagemRetiradaSelecionada(contagem.id_contagem)" (change)="alternarContagemRetirada(contagem, $any($event.target).checked)"></td>
                <td data-label="Contagem">{{ contagem.id_contagem }}</td>
                <td data-label="Estoque">{{ estoqueLabel(contagem.numero_nota) }}</td>
                <td data-label="Cliente">{{ contagem.cliente }}</td>
                <td data-label="Produto">{{ contagem.produtos_resumo || contagem.codigo_produto + ' - ' + contagem.produto }}</td>
                <td data-label="Data">{{ contagem.data_contagem | localDate }}</td>
                <td data-label="Responsaveis">{{ contagem.responsaveis }}</td>
                <td data-label="Total">{{ contagem.total_pecas }}</td>
              </tr>
            </tbody>
          </table>
          <p class="field-hint" *ngIf="!contagensParaRetiradaForm.length">Nenhuma contagem finalizada disponivel para retirada</p>

          <table class="size-table" *ngIf="resumoRetiradaGeral.length">
            <thead><tr><th>Estoque</th><th>Produto</th><th>Tamanho</th><th>Quantidade</th></tr></thead>
            <tbody>
              <tr *ngFor="let item of resumoRetiradaGeral">
                <td data-label="Estoque">{{ estoqueLabel(item.numero_nota) }}</td>
                <td data-label="Produto">{{ item.codigo_produto }} - {{ item.produto }}</td>
                <td data-label="Tamanho">{{ item.tamanho }}</td>
                <td data-label="Quantidade">{{ item.quantidade }}</td>
              </tr>
            </tbody>
          </table>

          <div class="form-actions">
            <button type="submit" [disabled]="!contagensRetirada.length">{{ retiradaEmEdicao ? 'Salvar alterações' : 'Registrar retirada' }}</button>
            <button class="ghost" type="button" (click)="limparRetirada()">Limpar</button>
          </div>
        </form>
      </section>
    </section>

    <table class="data-table" *ngIf="abaAtiva === 'consulta'">
      <thead>
        <tr><th>Cliente</th><th>Produto</th><th>Recebimento</th><th>Conferido</th><th>Contado</th><th>Retirado</th><th>Saldo</th><th>Status</th><th></th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let nota of notas">
          <td data-label="Cliente">{{ nota.cliente }}</td>
          <td data-label="Produto">{{ nota.produtos_resumo || nota.codigo_produto + ' - ' + nota.produto }}</td>
          <td data-label="Recebimento">{{ nota.data_recebimento | localDate }}</td>
          <td data-label="Conferido">{{ nota.total_conferido }}</td>
          <td data-label="Contado">{{ nota.total_contado }}</td>
          <td data-label="Retirado">{{ nota.total_retirado }}</td>
          <td data-label="Saldo">{{ nota.saldo_disponivel }}</td>
          <td data-label="Status"><span class="status" [class.finalizada]="nota.status === 'CONFERIDA'" [class.cancelada]="nota.status === 'CANCELADA' || nota.status === 'DIVERGENTE'">{{ statusLabel(nota.status) }}</span></td>
          <td class="actions" data-label="Ações">
            <a [routerLink]="['/notas', nota.id_nota]">Detalhe</a>
            <a *ngIf="!isFinal(nota)" [routerLink]="['/conferencias-entrada']" [queryParams]="{ ordem: nota.id_nota }">Conferência</a>
          </td>
        </tr>
      </tbody>
    </table>
  `
})
export class NotasComponent implements OnInit, DoCheck, OnDestroy {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  clientes: Cliente[] = [];
  produtos: Produto[] = [];
  notas: NotaTransferencia[] = [];
  retiradas: NotaRetirada[] = [];
  contagensDisponiveisRetirada: Contagem[] = [];
  contagensRetirada: Contagem[] = [];
  retiradaEmEdicao?: NotaRetirada;
  erro = '';
  mensagem = '';
  abaAtiva: 'consulta' | 'inclusao' | 'retiradas' = 'consulta';
  abaRetiradaAtiva: 'consulta' | 'edicao' = 'consulta';
  produtosComConferencia = new Set<number>();
  statusLabel = statusLabel;
  private avisoAtual = '';
  private avisoTimer?: ReturnType<typeof setTimeout>;

  form = this.fb.nonNullable.group({
    id_nota: [0],
    id_cliente: ['', Validators.required],
    numero_nota: [''],
    data_nota: [dataLocalParaInput(), Validators.required],
    data_recebimento: [dataLocalParaInput(), Validators.required],
    cor: [''],
    observacao: [''],
    produto_para_adicionar: [''],
    itens: this.fb.array([])
  });

  retiradaForm = this.fb.nonNullable.group({
    data_retirada: [dataLocalParaInput(), Validators.required],
    responsavel: ['', Validators.required],
    observacao: ['']
  });

  get itens() {
    return this.form.controls.itens as FormArray;
  }

  get produtosDisponiveis() {
    const selecionados = new Set(this.itens.controls.map((control) => Number(control.value.id_produto || 0)));
    return this.produtos.filter((produto) => !selecionados.has(produto.cod_produto));
  }

  get contagensParaRetiradaForm() {
    if (!this.retiradaEmEdicao) return this.contagensDisponiveisRetirada;

    const idNota = Number(this.retiradaEmEdicao.id_nota || 0);
    const map = new Map<number, Contagem>();
    this.contagensRetirada.forEach((contagem) => map.set(contagem.id_contagem, contagem));
    this.contagensDisponiveisRetirada
      .filter((contagem) => Number(contagem.id_nota || 0) === idNota)
      .forEach((contagem) => map.set(contagem.id_contagem, contagem));

    return [...map.values()].sort((a, b) => b.id_contagem - a.id_contagem);
  }

  ngOnInit() {
    this.api.clientes().subscribe((clientes) => (this.clientes = clientes.filter((cliente) => cliente.ativo)));
    this.api.produtos().subscribe((produtos) => (this.produtos = produtos.filter((produto) => produto.ativo)));
    this.carregar();
  }

  ngDoCheck() {
    const aviso = this.erro || this.mensagem;
    if (aviso === this.avisoAtual) return;

    this.avisoAtual = aviso;
    this.agendarFechamentoAviso(aviso);
  }

  ngOnDestroy() {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  carregar() {
    this.api.notas().subscribe((notas) => (this.notas = notas));
    this.carregarRetiradas();
    this.carregarContagensDisponiveisRetirada();
  }

  selecionarAba(aba: 'consulta' | 'inclusao' | 'retiradas') {
    this.abaAtiva = aba;
    this.erro = '';
    this.mensagem = '';
    if (aba === 'retiradas') {
      this.carregarRetiradas();
      this.carregarContagensDisponiveisRetirada();
    }
  }

  selecionarAbaRetirada(aba: 'consulta' | 'edicao') {
    this.abaRetiradaAtiva = aba;
    this.erro = '';
    this.mensagem = '';
    if (aba === 'consulta') this.carregarRetiradas();
    if (aba === 'edicao') this.carregarContagensDisponiveisRetirada();
  }

  carregarRetiradas() {
    this.api.retiradas().subscribe((retiradas) => (this.retiradas = retiradas));
  }

  carregarContagensDisponiveisRetirada() {
    this.api.contagensDisponiveisRetirada().subscribe((contagens) => (this.contagensDisponiveisRetirada = contagens));
  }

  fecharAviso() {
    this.erro = '';
    this.mensagem = '';
    this.avisoAtual = '';
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
  }

  salvar() {
    this.erro = '';
    this.mensagem = '';
    this.form.markAllAsTouched();
    const raw = this.form.getRawValue();
    const itens = raw.itens as OrdemEntradaFormItem[];
    if (!raw.id_cliente) {
      this.erro = 'Informe o cliente da ordem';
      return;
    }
    if (this.form.invalid || itens.length === 0) {
      this.erro = 'Informe ao menos um produto da ordem';
      return;
    }

    const payload = {
      ...raw,
      id_nota: raw.id_nota || undefined,
      id_cliente: Number(raw.id_cliente),
      id_produto: itens[0]?.id_produto,
      quantidade_nota: 0,
      produto_para_adicionar: undefined,
      itens: itens.map((item) => ({
        id_produto: Number(item.id_produto || 0),
        quantidade: 0,
        valor_unitario: 0
      }))
    };

    this.api.salvarNota(payload).subscribe({
      next: (nota) => {
        this.limpar();
        this.mensagem = `Ordem ${nota.numero_nota} salva com sucesso`;
        this.abaAtiva = 'consulta';
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao salvar ordem')
    });
  }

  editar(nota: NotaTransferencia) {
    this.api.nota(nota.id_nota).subscribe((detalhe) => {
      this.abaAtiva = 'inclusao';
      this.erro = '';
      this.mensagem = '';
      this.form.patchValue({
        id_nota: detalhe.id_nota,
        id_cliente: String(detalhe.id_cliente),
        numero_nota: detalhe.numero_nota,
        data_nota: String(detalhe.data_nota).slice(0, 10),
        data_recebimento: String(detalhe.data_recebimento).slice(0, 10),
        cor: detalhe.cor || '',
        observacao: detalhe.observacao || '',
        produto_para_adicionar: ''
      });
      this.itens.clear();
      const idsProdutos = [...new Set((detalhe.itens || []).map((item) => Number(item.id_produto || 0)).filter(Boolean))];
      idsProdutos.forEach((idProduto) => this.itens.push(this.itemGroup(idProduto)));
      this.produtosComConferencia = new Set(
        (detalhe.saldos || [])
          .filter((item) => Number(item.total_conferido || 0) > 0)
          .map((item) => Number(item.id_produto || 0))
          .filter(Boolean)
      );
    });
  }

  limpar() {
    this.erro = '';
    this.mensagem = '';
    this.form.reset({
      id_nota: 0,
      id_cliente: '',
      numero_nota: '',
      data_nota: dataLocalParaInput(),
      data_recebimento: dataLocalParaInput(),
      cor: '',
      observacao: '',
      produto_para_adicionar: ''
    });
    this.itens.clear();
    this.produtosComConferencia.clear();
  }

  adicionarProduto() {
    const idProduto = Number(this.form.controls.produto_para_adicionar.value || 0);
    if (!idProduto) return;
    if (this.itens.controls.some((item) => Number(item.value.id_produto || 0) === idProduto)) return;

    this.itens.push(this.itemGroup(idProduto));
    this.form.controls.produto_para_adicionar.setValue('');
  }

  removerProduto(index: number) {
    const idProduto = Number(this.itens.at(index)?.value?.id_produto || 0);
    if (this.produtoTemConferencia(idProduto)) {
      this.erro = 'Nao e permitido remover produto com conferencia de entrada';
      return;
    }

    this.itens.removeAt(index);
  }

  produtoTemConferencia(idProduto: number) {
    return this.produtosComConferencia.has(Number(idProduto || 0));
  }

  reabrir(nota: NotaTransferencia) {
    const confirmar = window.confirm(`Reabrir a ordem ${nota.numero_nota}?`);
    if (!confirmar) return;

    this.erro = '';
    this.mensagem = '';
    this.api.reabrirNota(nota.id_nota).subscribe({
      next: (ordem) => {
        this.mensagem = `Ordem ${ordem.numero_nota} reaberta com sucesso`;
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao reabrir ordem')
    });
  }

  registrarRetiradaGeral() {
    if (!this.contagensRetirada.length) {
      this.erro = 'Selecione ao menos uma contagem para a retirada';
      return;
    }
    this.erro = '';
    this.mensagem = '';
    this.retiradaForm.markAllAsTouched();
    const raw = this.retiradaForm.getRawValue();
    if (!String(raw.responsavel || '').trim()) {
      this.erro = 'Informe o responsavel pela entrega';
      return;
    }

    if (this.retiradaEmEdicao) {
      const total = this.resumoRetiradaGeral.reduce((sum, item) => sum + Number(item.quantidade || 0), 0);
      const contagens = this.contagensRetirada.map((contagem) => contagem.id_contagem).join(', ');
      const confirmar = window.confirm(`Salvar retirada #${this.retiradaEmEdicao.id_retirada} com as contagens ${contagens}, totalizando ${total} pecas?`);
      if (!confirmar) return;

      this.api.editarRetirada(this.retiradaEmEdicao.id_retirada, {
        ...raw,
        id_contagens: this.contagensRetirada.map((contagem) => contagem.id_contagem)
      }).subscribe({
        next: () => {
          this.mensagem = 'Retirada atualizada com sucesso';
          this.limparRetirada();
          this.abaRetiradaAtiva = 'consulta';
          this.carregar();
        },
        error: (error) => (this.erro = error.error?.message || 'Erro ao editar retirada')
      });
      return;
    }

    const total = this.resumoRetiradaGeral.reduce((sum, item) => sum + Number(item.quantidade || 0), 0);
    const contagens = this.contagensRetirada.map((contagem) => contagem.id_contagem).join(', ');
    const ordens = new Set(this.contagensRetirada.map((contagem) => contagem.numero_nota).filter(Boolean)).size;
    const confirmar = window.confirm(`Confirmar retirada das contagens ${contagens}, totalizando ${total} pecas em ${ordens} ordem(ns)?`);
    if (!confirmar) return;

    this.api.registrarRetiradaPorContagens({
      ...raw,
      id_contagens: this.contagensRetirada.map((contagem) => contagem.id_contagem)
    }).subscribe({
      next: (resposta) => {
        const retiradas = Array.isArray(resposta) ? resposta : resposta ? [resposta] : [];
        if (!retiradas.length) {
          this.erro = 'A retirada nao foi retornada pela API. Atualize a consulta e tente novamente.';
          return;
        }

        // Atualiza a tela antes de tentar abrir o PDF. Popup bloqueado nao pode
        // impedir que a retirada registrada apareca na consulta.
        this.limparRetirada();
        this.abaRetiradaAtiva = 'consulta';
        this.mensagem = retiradas.length > 1
          ? `${retiradas.length} retiradas registradas com sucesso`
          : 'Retirada registrada com sucesso';
        this.carregar();
        retiradas.forEach((retirada) => {
          if (retirada?.id_nota && retirada?.id_retirada) {
            this.api.abrirRetirada(retirada.id_nota, retirada.id_retirada);
          }
        });
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao registrar retirada')
    });
  }

  alternarContagemRetirada(contagem: Contagem, selecionada: boolean) {
    this.erro = '';
    this.mensagem = '';
    if (!selecionada) {
      this.contagensRetirada = this.contagensRetirada.filter((item) => item.id_contagem !== contagem.id_contagem);
      return;
    }
    if (this.contagemRetiradaSelecionada(contagem.id_contagem)) return;
    if (this.retiradaEmEdicao && Number(contagem.id_nota || 0) !== Number(this.retiradaEmEdicao.id_nota || 0)) {
      this.erro = 'A retirada so pode ter contagens da mesma ordem de entrada';
      return;
    }

    this.api.contagem(contagem.id_contagem).subscribe({
      next: (detalhe) => {
        this.contagensRetirada = [...this.contagensRetirada, detalhe].sort((a, b) => b.id_contagem - a.id_contagem);
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao carregar contagem')
    });
  }

  contagemRetiradaSelecionada(idContagem: number) {
    return this.contagensRetirada.some((contagem) => contagem.id_contagem === idContagem);
  }

  limparRetirada() {
    this.retiradaEmEdicao = undefined;
    this.contagensRetirada = [];
    this.retiradaForm.reset({
      data_retirada: dataLocalParaInput(),
      responsavel: '',
      observacao: ''
    });
  }

  editarRetirada(retirada: NotaRetirada) {
    this.retiradaEmEdicao = retirada;
    this.contagensRetirada = [];
    this.abaRetiradaAtiva = 'edicao';
    this.erro = '';
    this.mensagem = '';
    this.retiradaForm.reset({
      data_retirada: String(retirada.data_retirada || dataLocalParaInput()).slice(0, 10),
      responsavel: retirada.responsavel || '',
      observacao: retirada.observacao || ''
    });
    this.carregarContagensDisponiveisRetirada();
    const idsContagens = retirada.id_contagens?.length ? retirada.id_contagens : ([retirada.id_contagem].filter(Boolean) as number[]);
    idsContagens.forEach((idContagem) => {
      this.api.contagem(idContagem).subscribe({
        next: (detalhe) => {
          if (this.retiradaEmEdicao?.id_retirada !== retirada.id_retirada) return;
          this.contagensRetirada = [...this.contagensRetirada, detalhe]
            .filter((contagem, index, lista) => lista.findIndex((item) => item.id_contagem === contagem.id_contagem) === index)
            .sort((a, b) => b.id_contagem - a.id_contagem);
        },
        error: (error) => (this.erro = error.error?.message || 'Erro ao carregar contagem da retirada')
      });
    });
  }

  excluirRetirada(retirada: NotaRetirada) {
    const contagens = this.contagensRetiradaLabel(retirada) || '-';
    const confirmar = window.confirm(`Excluir a retirada #${retirada.id_retirada} e liberar as contagens ${contagens} para nova retirada?`);
    if (!confirmar) return;

    this.erro = '';
    this.mensagem = '';
    this.api.excluirRetirada(retirada.id_retirada).subscribe({
      next: () => {
        if (this.retiradaEmEdicao?.id_retirada === retirada.id_retirada) {
          this.limparRetirada();
          this.abaRetiradaAtiva = 'consulta';
        }
        this.mensagem = 'Retirada excluida e contagens liberadas para nova retirada';
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao excluir retirada')
    });
  }

  imprimirRetirada(retirada: NotaRetirada) {
    this.api.abrirRetirada(retirada.id_nota, retirada.id_retirada);
  }

  contagensRetiradaLabel(retirada: { id_contagem?: number; id_contagens?: number[] }) {
    return (retirada.id_contagens?.length ? retirada.id_contagens : [retirada.id_contagem].filter(Boolean)).join(', ');
  }

  totalRetirada(itens: NotaTransferenciaItem[] = []) {
    return itens.reduce((sum, item) => sum + Number(item.quantidade || 0), 0);
  }

  totalItemContagem(item: ContagemItem) {
    return Number(item.total ?? Number(item.quantidade || 0) + Number(item.defeitos || 0));
  }

  estoqueLabel(numero?: string) {
    return String(numero || '').startsWith('ESTOQUE-') ? 'Estoque do cliente' : String(numero || '-');
  }

  get resumoRetiradaGeral() {
    const resumo = new Map<string, NotaTransferenciaItem & { numero_nota?: string }>();
    this.contagensRetirada.forEach((contagem) => {
      (contagem.itens || []).forEach((item) => {
        const key = `${contagem.id_nota || 0}|${Number(item.id_produto || 0)}|${item.tamanho || ''}`;
        const atual = resumo.get(key) || {
          numero_nota: contagem.numero_nota,
          id_produto: item.id_produto,
          codigo_produto: item.codigo_produto,
          produto: item.produto,
          tamanho: item.tamanho,
          quantidade: 0
        };
        atual.quantidade += this.totalItemContagem(item);
        resumo.set(key, atual);
      });
    });

    return [...resumo.values()].sort((a, b) => {
      const ordem = String(a.numero_nota || '').localeCompare(String(b.numero_nota || ''));
      const produto = String(a.codigo_produto || '').localeCompare(String(b.codigo_produto || ''));
      return ordem || produto || String(a.tamanho || '').localeCompare(String(b.tamanho || ''));
    });
  }

  isFinal(nota: NotaTransferencia) {
    return ['CONFERIDA', 'DIVERGENTE', 'FINALIZADA', 'CANCELADA'].includes(nota.status);
  }

  permiteReabrir(nota: NotaTransferencia) {
    return ['CONFERIDA', 'DIVERGENTE', 'FINALIZADA'].includes(nota.status);
  }

  produtoLabel(idProduto: number) {
    const produto = this.produtos.find((item) => item.cod_produto === Number(idProduto || 0));
    return produto ? `${produto.codigo_produto} - ${produto.descricao}` : idProduto;
  }

  private itemGroup(idProduto: number) {
    return this.fb.nonNullable.group({
      id_produto: [idProduto]
    });
  }

  private agendarFechamentoAviso(aviso: string) {
    if (this.avisoTimer) clearTimeout(this.avisoTimer);
    if (!aviso) return;

    this.avisoTimer = setTimeout(() => this.fecharAviso(), 20000);
  }
}
